import { IDeliveryType, IExpression, IOtherControl, IPackage } from "../models/configuration/plugin/IConfiguration";
import { CX_Docutech_Selected_Package, CX_Docutech_Delivery_Types, AutoSelectWhen, ValidDeliveryTypes } from "./constants";
import { conditionMap } from "../constants/ExpreessionConstants";

/**
 * Escapes regex metacharacters in a string to make it safe for use in RegExp constructor
 * @param input - The input to escape
 * @returns The escaped string
 */
const escapeRegExp = (input: string): string => {
    return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Normalizes delivery method IDs by filtering out invalid entries and joining valid IDs
 * @param selectedDeliveryMethods - Array of delivery method objects
 * @returns Comma-separated string of valid delivery method IDs, or null if none
 */
export const getSelectedDeliveryTypeIds = (selectedDeliveryMethods?: IDeliveryType[]): string | null => {
    if (!selectedDeliveryMethods || selectedDeliveryMethods.length === 0) {
        return null;
    }
    
    const validIds = selectedDeliveryMethods
        .filter(dm => dm?.Id && dm.Id.trim() !== '')
        .map(dm => dm.Id.trim());
    
    // Remove duplicates and sort to produce stable IDs for expression comparisons
    const uniqueIds = Array.from(new Set(validIds)).sort();
    
    return uniqueIds.length > 0 ? uniqueIds.join(',') : null;
};

/**
 * Enhances field ID value pairs by adding the selected package value
 * @param fieldIdValuePairs - Original field ID value pairs
 * @param selectedPackage - Currently selected package value
 * @returns Enhanced field ID value pairs with selected package included
 */
export const enhanceFieldIdValuePairs = (
    fieldIdValuePairs: { [key: string]: string },
    selectedPackage: string | null,
    selectedDeliveryMethods?: IDeliveryType[]
): { [key: string]: string } => {
    const enhanced = { ...fieldIdValuePairs };
    if (selectedPackage) {
        enhanced[CX_Docutech_Selected_Package] = selectedPackage;
    }
    if (selectedDeliveryMethods && selectedDeliveryMethods.length > 0) {
        const deliveryTypeIds = getSelectedDeliveryTypeIds(selectedDeliveryMethods);
        if (deliveryTypeIds) {
            enhanced[CX_Docutech_Delivery_Types] = deliveryTypeIds;
        }
    } else if (Array.isArray(selectedDeliveryMethods) && selectedDeliveryMethods.length === 0) {
        delete enhanced[CX_Docutech_Delivery_Types];
    }
    return enhanced;
};

export const GetExpressionEvaluationResults = async (expressions: IExpression[], fieldIdValueIdPairsFromOrigin: { [key: string]: string }, selectedPackageType: string | null): Promise<IExpression[]> => {
    const expressionList = expressions;
    try {
        return expressionList?.map((exp: IExpression) => {
            return {
                ...exp,
                ExpressionString: addRegexExp(exp.ExpressionString, fieldIdValueIdPairsFromOrigin, selectedPackageType)
            };
        });
    }
    catch (e) {
        console.error("Error while calling expression service:", e);
    }
    return expressionList;
}

export const addRegexExp = (expression: string, fieldIdValueIdPairsFromOrigin: { [key: string]: string }, selectedPackageType: string | null, selectedDeliveryMethods?: IDeliveryType[]) => {
    let expString = expression;
    try {
        if (expString?.trim()) {
            expString = expString.replace(/\s*\n\s*/g, ' '); //Combine multi-line expressions into a single line

            if (expString.includes(`[${CX_Docutech_Selected_Package}]`)) {
                const placeholderValue = selectedPackageType || fieldIdValueIdPairsFromOrigin[CX_Docutech_Selected_Package];
                
                if (placeholderValue && placeholderValue !== 'undefined') {
                    expString = expString.replace(new RegExp(`\\[${escapeRegExp(CX_Docutech_Selected_Package)}\\]`, 'g'), JSON.stringify(placeholderValue));
                }
            }

            // Replace CX.DOCUTECH.DELIVERYTYPES placeholder with selected delivery method IDs
            if (expString.includes(`[${CX_Docutech_Delivery_Types}]`)) {
                let deliveryTypeIds: string | null = null;
                
                // Try to get delivery types from direct parameter or field context (similar to package logic)
                if (selectedDeliveryMethods?.length) {
                    deliveryTypeIds = getSelectedDeliveryTypeIds(selectedDeliveryMethods);
                } else if (fieldIdValueIdPairsFromOrigin[CX_Docutech_Delivery_Types]) {
                    deliveryTypeIds = fieldIdValueIdPairsFromOrigin[CX_Docutech_Delivery_Types];
                }
                
                if (deliveryTypeIds) {
                    expString = expString.replace(new RegExp(`\\[${escapeRegExp(CX_Docutech_Delivery_Types)}\\]`, 'g'), JSON.stringify(deliveryTypeIds));
                }
            }

            expString = replaceExpressionFieldNameByFieldValue(expString, fieldIdValueIdPairsFromOrigin);

            expString = expString.replace(/\[([^\]]+)\]/g, `""`); //Replace any remaining placeholders with an empty string
        }
    }
    catch (e) {
        console.error("Error while calling addRegexExp:", e);
    }
    return expString;
}

export const replaceExpressionFieldNameByFieldValue = (expString: string, fieldIdValueIdPairsFromOrigin: { [key: string]: string }) => {
    //Extract unique field Names from expression
    const fieldRegex = /\[(.*?)\]/gi;
    const matchArray: string[] = [];
    let match;
    while ((match = fieldRegex.exec(expString)) !== null) {
        matchArray.push(match[1]);
    }
    const expressionFields = Array.from(new Set(matchArray));

    //Replace field values in the expression   
    expressionFields?.forEach(key => {
        const value = fieldIdValueIdPairsFromOrigin[key];
        if (value) {
            // Check if the value corresponds to a boolean field (true/false).
            // If it is, replace the field conditions with all possible boolean representations (e.g. yn format(value starts with "y" or "n")/true/false)..
            // Otherwise, replace the field placeholder with the actual value
            if (conditionMap.hasOwnProperty(value)) {
                expString = replaceConditions(value, key, expString);
            }
            else {
                const regex = new RegExp(`\\[${escapeRegExp(key)}\\]`, 'g');
                expString = expString.replace(regex, JSON.stringify(value));
            }
        };
    });

    return expString;
}

export const replaceConditions = (booleanValue: string, field: string, expString: string) => {
    // Regex to match both quoted and unquoted right-hand side values
    const escapedField = escapeRegExp(field);
    const opPattern = new RegExp(
        `(\\[${escapedField}\\])\\s*(=|<>)\\s*(?:"(y|yes|t|true|n|no|f|false)"|\\b(y|yes|t|true|n|no|f|false)\\b)`,
        'gi'
    );

    let result = expString.replace(opPattern, (match, left, op, quotedRight, unquotedRight) => {
        const rightNorm = (quotedRight || unquotedRight).toLowerCase();
        if (conditionMap.True.includes(rightNorm)) {
            return `${booleanValue} ${op} True`;
        }
        if (conditionMap.False.includes(rightNorm)) {
            return `${booleanValue} ${op} False`;
        }
        return match; // Return the original match if no replacement is made
    });

    // Perform the fieldPattern replacement only if no other replacements were made
    if (result === expString) {
        const fieldPattern = new RegExp(`\\[${escapeRegExp(field)}\\]`, 'gi');
        result = expString.replace(fieldPattern, JSON.stringify(booleanValue));
    }

    return result;
};

export const hasAutoEnableWhen = (item: any): item is { AutoSelectWhen: string } => {
    return item && typeof item.AutoSelectWhen === "string";
}

export const isDataTypePackage = (item: any): item is IPackage => {
    return (
        item &&
        typeof item.AutoSelectWhen === "string" &&
        Array.isArray(item.ValidDeliveryTypes) &&
        item.ValidDeliveryTypes?.every((value: string) => typeof value === "string")
    );
}

export const isDataTypeDeliveryType = (item: any): item is IDeliveryType => {
    return (
        item &&
        typeof item.AutoSelectWhen === "string" &&
        (!(ValidDeliveryTypes in item) || item.ValidDeliveryTypes === undefined)
    );
}

export const isDataTypeOtherControl = (item: any): item is IOtherControl => {
    return (
        item &&
        !(AutoSelectWhen in item) &&
        !(ValidDeliveryTypes in item)
    );
}


// Check if expression contains CX.DOCUTECH.SELECTED.PACKAGE reference
export const isPackageBasedExpression = (expression: string): boolean => {
    return expression.toUpperCase().includes(CX_Docutech_Selected_Package.toUpperCase());
}